import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  SupportRadioIcon,
  SupportTvIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrentUsers } from '@/components/CurrentUsers/CurrentUsers';
import { RenderEnrichementsIcons } from '@/components/RenderEnrichementsIcons/RenderEnrichementsIcons';
import { StartTimeEndTimePart } from '@/components/StartTimeEndTimePart/StartTimeEndTimePart';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { SEGMENT_REVIEW_STATES } from '@/constants';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { ChannelMediaTypeEnum, ChannelType } from '@/features/broadcasters/types';
import { GlobalStatusEnrichmentEnum, TSegment } from '@/features/segments/types';
import { wrapperIconByStatus } from '@/helpers/wrapperIconStatus';
import { useSegmentPresence } from '@/providers/websocket';
import { getProgramBroadcastHours } from '@/utils/date';

type TBroadcasterScheduleProgramMiniCardCardHeaderProps = {
  item: TSegment;
  channel?: ChannelType;
  /**
   * The emission this card is being listed under, when the view is reviewing one.
   * Absent in the global segments view, which lists a channel's blocks rather than
   * an emission's.
   */
  programBroadcast?: TProgramBroadcast;
};

export function BroadcasterScheduleProgramMiniCardCardHeader({
  item,
  channel,
  programBroadcast,
}: TBroadcasterScheduleProgramMiniCardCardHeaderProps) {
  const userConnectedInSegments = useSegmentPresence();
  const { t } = useTranslation();
  const { compactUis } = useBroadcastScheduleProgram();

  const programDetails = useMemo(() => {
    /**
     * The emission the card is listed under, when the segment names none of its own.
     *
     * A block absorbed by moving an edge of an emission is returned before the
     * association is written — the window moves in the request, the programme is
     * attached by a queue afterwards (RCOMM-6562) — so it arrives naming neither
     * emission nor programme, and the card denied an association the server had
     * already arranged. Under a review there is an answer without asking the block:
     * this strip lists one emission's own segments, so the block belongs to the
     * emission being reviewed.
     */
    const reviewed = programBroadcast
      ? [
          [programBroadcast.name, getProgramBroadcastHours(programBroadcast)]
            .filter(Boolean)
            .join(' · '),
        ]
      : null;

    // Outside a review nothing narrows it down, and a block with no programme at all
    // genuinely has none.
    if (!item.programs?.length) return reviewed ?? [t('Franja sin programa asociado')];

    // A block that names a programme is only this emission's if it names *its*
    // programme. The strip includes the blocks that open and close the emission
    // (RCOMM-5948), and one of those may state the neighbouring programme and nothing
    // else; relabelling it with the emission being reviewed would pick for it, which is
    // the opposite of what the frontier rule below does.
    const namesReviewedProgram = item.programs.some(
      program => program.id === programBroadcast?.program_id
    );

    // A segment on a frontier belongs to two emissions, and both are true of it, so
    // both are shown rather than one being picked. Sorted by their own start so they
    // read in the order they aired: the payload follows `program_values`, whose order
    // comes from `programs` and means nothing.
    const emissions = [...(item.program_broadcasts ?? [])].sort((a, b) =>
      a.start_datetime.localeCompare(b.start_datetime)
    );

    // The emission names itself: the payload carries no programme identifier, so it
    // cannot be traced back to an entry of `programs`, and matching by name would be
    // guesswork. With no emission at all there is nothing to disambiguate by, so the
    // first programme is as good as any — and the window stays empty rather than
    // being invented.
    // The channel is not repeated here: the row below already names it.
    if (!emissions.length) {
      return namesReviewedProgram && reviewed ? reviewed : [item.programs[0].name];
    }

    return emissions.map(emission =>
      [emission.name, getProgramBroadcastHours(emission)].filter(Boolean).join(' · ')
    );
  }, [item.programs, item.program_broadcasts, programBroadcast, t]);

  const globalStatusMapper: Record<
    string,
    {
      text: string;
      className?: string;
    }
  > = {
    [GlobalStatusEnrichmentEnum.ERROR]: {
      text: t('Error'),
      className: 'bg-feedback-error-20',
    },
    [GlobalStatusEnrichmentEnum.PROCESSED]: {
      text: t('Procesado'),
      className: 'bg-extra-70',
    },
    [GlobalStatusEnrichmentEnum.PROCESSING]: {
      text: t('Procesando'),
      className: 'bg-extra-80 animate-fade-in-out duration-1000',
    },
    [GlobalStatusEnrichmentEnum.NOT_PROCESSED]: {
      text: t('No procesado'),
    },
  };

  const { text: statusText, className: statusClassName } =
    globalStatusMapper[item.global_status_enrichment];

  return (
    <div className="flex w-full flex-row flex-wrap items-center gap-2 pr-2">
      <div
        className="ml-0.5 flex w-full items-center justify-between gap-2"
        data-id="program-details"
      >
        <div className="flex flex-col">
          {programDetails.map(line => (
            <Typography
              key={line}
              fontSize={FontSizeEnum.Sm}
              color={FontColorEnum.Dark10}
              weight={FontWeightEnum.Semibold}
            >
              {line}
            </Typography>
          ))}
        </div>
        <div className="flex justify-end gap-2 md:flex-1">
          <RenderEnrichementsIcons
            enrichments={{
              ocr: item.parameters?.enrichments?.ocr,
              stt: item.parameters?.enrichments?.stt,
              logos: item.parameters?.enrichments?.logos,
              peoples: item.parameters?.enrichments?.peoples,
              advertising: item.parameters?.enrichments?.advertising,
            }}
            statusEnrichments={{
              ocr: item.parameters?.status_enrichments?.ocr?.status,
              stt: item.parameters?.status_enrichments?.stt?.status,
              logos: item.parameters?.status_enrichments?.logos?.status,
              peoples: item.parameters?.status_enrichments?.peoples?.status,
              advertising: item.parameters?.status_enrichments?.advertising?.status,
            }}
            classname="items-center"
            iconSize={IconSizeEnum.SM}
          />
          {userConnectedInSegments[item.id] && (
            <CurrentUsers ids={userConnectedInSegments[item.id]} />
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center">
        <div className="flex w-full flex-wrap items-center gap-2">
          <Tooltip help={statusText} grow>
            {wrapperIconByStatus(
              item.media_type.includes(ChannelMediaTypeEnum.TV) ? (
                <SupportTvIcon />
              ) : (
                <SupportRadioIcon />
              ),
              clsx(statusClassName, statusClassName && '!-right-0.5', '!h-1 !w-1')
            )}
          </Tooltip>
          <StatusSeenChipInTable
            item={item}
            review_state={item.review_state}
            reviewStates={SEGMENT_REVIEW_STATES}
          />
          {compactUis ? (
            <span className="max-w-32 overflow-hidden text-ellipsis whitespace-nowrap font-inter text-caption font-bold text-text-dark-10">
              {channel?.name}
            </span>
          ) : (
            <Typography
              color={FontColorEnum.Dark10}
              fontSize={FontSizeEnum.Md}
              weight={FontWeightEnum.Bold}
            >
              {channel?.name}
            </Typography>
          )}
          <StartTimeEndTimePart
            startDateTime={item?.start_datetime}
            endDateTime={item.end_datetime}
            showEndDatePart={false}
          />
        </div>
      </div>
    </div>
  );
}
